import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Select, TextInput, Textarea, Button, Group, Text } from '@mantine/core';
import { Bug, Lightbulb, MessageCircle, Send } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useNavigation } from '../contexts/NavigationContext';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: FeedbackData) => Promise<void>;
}

export interface FeedbackData {
  type: 'bug' | 'feature' | 'general';
  title: string;
  description: string;
  userEmail: string;
  userName: string;
  contextInfo: {
    currentMode: string;
    userAgent: string;
    url: string;
    timestamp: string;
  };
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [type, setType] = useState<'bug' | 'feature' | 'general'>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useUser();
  const { currentMode } = useNavigation();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setType('bug');
      setTitle('');
      setDescription('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;

    setIsSubmitting(true);
    try {
      const feedbackData: FeedbackData = {
        type,
        title: title.trim(),
        description: description.trim(),
        userEmail: currentUser?.email || '',
        userName: currentUser?.name || 'Anonymous',
        contextInfo: {
          currentMode: currentMode,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        }
      };

      await onSubmit(feedbackData);
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: <Bug size={16} />, description: 'Report a problem or error' },
    { value: 'feature', label: 'Feature Request', icon: <Lightbulb size={16} />, description: 'Suggest a new feature or improvement' },
    { value: 'general', label: 'General Feedback', icon: <MessageCircle size={16} />, description: 'Share your thoughts or ask questions' },
  ];

  const selectedType = feedbackTypes.find(t => t.value === type);

  const actionButtons = (
    <Group gap="sm">
      <Button
        variant="outline"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={!title.trim() || !description.trim() || isSubmitting}
        loading={isSubmitting}
        leftSection={<Send size={16} />}
      >
        Send Feedback
      </Button>
    </Group>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Help & Feedback"
      hideCloseButton
      actionButtons={actionButtons}
    >
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <Text size="sm" c="var(--app-text-secondary)" mb="md">
          We'd love to hear from you! Report bugs, suggest features, or share general feedback.
        </Text>

        <Select
          label="Feedback Type"
          value={type}
          onChange={(value) => setType(value as 'bug' | 'feature' | 'general')}
          data={feedbackTypes.map(t => ({ value: t.value, label: t.label }))}
          mb="md"
          leftSection={selectedType?.icon}
        />

        {selectedType && (
          <Text size="xs" c="var(--app-text-secondary)" mb="md" style={{ marginTop: '-8px' }}>
            {selectedType.description}
          </Text>
        )}

        <TextInput
          label="Title"
          placeholder={
            type === 'bug' ? 'Brief description of the problem...' :
            type === 'feature' ? 'What feature would you like to see?' :
            'What would you like to tell us?'
          }
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          mb="md"
          required
        />

        <Textarea
          label="Description"
          placeholder={
            type === 'bug' ? 'Please describe what happened, what you expected, and any steps to reproduce the issue...' :
            type === 'feature' ? 'Describe the feature and how it would help you...' :
            'Share your thoughts, questions, or suggestions...'
          }
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          rows={6}
          mb="md"
          required
        />

        <Text size="xs" c="var(--app-text-secondary)">
          Technical details (current page, browser info) will be automatically included to help us assist you better.
        </Text>
      </div>
    </Modal>
  );
};